'use client'

import { useState, useEffect } from 'react'
import { User } from '@/types'
import { useUser } from '@/hooks/useUser'
import { useToast } from '@/hooks/useToast'
import { getApiUrl } from '@/lib/apiUrl'



interface AuditLog {
  id: string
  user_id: string
  action: string
  resource_type: 'user' | 'property' | 'application' | 'payment' | 'system'
  resource_id?: string
  details: string
  ip_address?: string
  user_agent?: string
  created_at: string
  user?: User
}

interface AuditLogsProps {
  onUpdate: () => void
}

export default function AuditLogs({ }: AuditLogsProps) {
  const { token } = useUser()
  const toast = useToast()
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'user' | 'property' | 'application' | 'payment' | 'system'>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [dateRange, setDateRange] = useState<'today' | 'week' | 'month' | 'all'>('all')

  // Load audit logs
  const loadLogs = async () => {
    setLoading(true)
    try {
      if (!token) {
        setLogs([])
        return
      }

      const apiUrl = getApiUrl()
      const params = new URLSearchParams()
      params.set('resource_type', filter)
      params.set('dateRange', dateRange)
      if (searchTerm) params.set('search', searchTerm)

      const response = await fetch(`${apiUrl}/admin/audit-logs?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload?.message || 'Failed to load audit logs')
      }

      setLogs(payload?.logs || [])
    } catch (error) {
      console.error('Error:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to load audit logs')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadLogs()
  }, [filter, searchTerm, dateRange])

  // Get action color
  const getActionColor = (action: string) => {
    if (action.includes('create') || action.includes('add')) {
      return 'bg-green-100 text-green-800'
    } else if (action.includes('update') || action.includes('edit')) {
      return 'bg-blue-100 text-blue-800'
    } else if (action.includes('delete') || action.includes('remove')) {
      return 'bg-red-100 text-red-800'
    } else if (action.includes('login') || action.includes('auth')) {
      return 'bg-purple-100 text-purple-800'
    } else {
      return 'bg-gray-100 text-gray-800'
    }
  }

  // Get resource type color
  const getResourceTypeColor = (type: string) => {
    switch (type) {
      case 'user':
        return 'bg-blue-100 text-blue-800'
      case 'property':
        return 'bg-green-100 text-green-800'
      case 'application':
        return 'bg-yellow-100 text-yellow-800'
      case 'payment':
        return 'bg-purple-100 text-purple-800'
      case 'system':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString()
  }

  // Calculate stats
  const totalLogs = logs.length
  const todayLogs = logs.filter(log => {
    const today = new Date()
    const logDate = new Date(log.created_at)
    return logDate.toDateString() === today.toDateString()
  }).length

  const userActions = logs.filter(log => log.resource_type === 'user').length
  const propertyActions = logs.filter(log => log.resource_type === 'property').length
  const systemActions = logs.filter(log => log.resource_type === 'system').length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Audit Logs</h2>
        <div className="flex space-x-2">
          <input
            type="text"
            placeholder="Search logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Types</option>
            <option value="user">User</option>
            <option value="property">Property</option>
            <option value="application">Application</option>
            <option value="payment">Payment</option>
            <option value="system">System</option>
          </select>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
          </select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Total Logs</h3>
          <p className="text-3xl font-bold text-blue-600">{totalLogs}</p>
          <p className="text-sm text-gray-500">All time</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Today</h3>
          <p className="text-3xl font-bold text-green-600">{todayLogs}</p>
          <p className="text-sm text-gray-500">Actions today</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">User Actions</h3>
          <p className="text-3xl font-bold text-purple-600">{userActions}</p>
          <p className="text-sm text-gray-500">User-related</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Property Actions</h3>
          <p className="text-3xl font-bold text-orange-600">{propertyActions}</p>
          <p className="text-sm text-gray-500">Property-related</p>
        </div>
      </div>

      {/* Audit Logs Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Activity Log</h3>
        </div>
        
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading audit logs...</p>
          </div>
        ) : logs.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Resource
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    IP Address
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatTimestamp(log.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.user?.email || 'System'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getResourceTypeColor(log.resource_type)}`}>
                          {log.resource_type}
                        </span>
                        {log.resource_id && (
                          <span className="text-xs text-gray-500">#{log.resource_id}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                      {log.details}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {log.ip_address || 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            <p>No audit logs found</p>
            <p className="text-sm">No activity matches your current filters</p>
          </div>
        )}
      </div>

      {/* Export Options */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Export Options</h3>
        <div className="flex space-x-4">
          <button
            onClick={() => {
              const csvContent = logs.map(log => 
                `${log.created_at},${log.user?.email || 'System'},${log.action},${log.resource_type},${log.details},${log.ip_address || 'N/A'}`
              ).join('\n')
              
              const csvHeader = 'Timestamp,User,Action,Resource Type,Details,IP Address\n'
              const blob = new Blob([csvHeader + csvContent], { type: 'text/csv' })
              const url = window.URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`
              a.click()
            }}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
          >
            Export to CSV
          </button>
          
          <button
            onClick={() => {
              const jsonContent = JSON.stringify(logs, null, 2)
              const blob = new Blob([jsonContent], { type: 'application/json' })
              const url = window.URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.json`
              a.click()
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Export to JSON
          </button>
        </div>
      </div>

      {/* Recent Activity Summary */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Activity Summary</h3>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900">Most Active User</h4>
              <p className="text-sm text-gray-600">
                {(() => {
                  const userCounts = logs.reduce((acc, log) => {
                    const email = log.user?.email || 'System'
                    acc[email] = (acc[email] || 0) + 1
                    return acc
                  }, {} as Record<string, number>)
                  
                  const mostActive = Object.entries(userCounts).sort(([,a], [,b]) => b - a)[0]
                  return mostActive ? `${mostActive[0]} (${mostActive[1]} actions)` : 'No data'
                })()}
              </p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900">Most Common Action</h4>
              <p className="text-sm text-gray-600">
                {(() => {
                  const actionCounts = logs.reduce((acc, log) => {
                    acc[log.action] = (acc[log.action] || 0) + 1
                    return acc
                  }, {} as Record<string, number>)
                  
                  const mostCommon = Object.entries(actionCounts).sort(([,a], [,b]) => b - a)[0]
                  return mostCommon ? `${mostCommon[0]} (${mostCommon[1]} times)` : 'No data'
                })()}
              </p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900">System Actions</h4>
              <p className="text-sm text-gray-600">{systemActions} system-level actions</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 


